import { useMemo, useCallback } from 'react';
import { BodyText } from '@minecraft.atoms';
import { AsyncTypeaheadInput, FieldWrapper, FieldWrapperValidation } from '@minecraft.molecules';
import { captureExceptionEvent } from '@blocs.observability';
import { PlacesApiProvider } from './PlacesApiProvider';
import { PLACES_MIN_QUERY_LENGTH } from './constants';
import { usePlacesAutocomplete } from './usePlacesAutocomplete';
import { PlaceSuggestion, SuggestedAddress } from './types';

export type AddressChange =
  | {
      line1Only: true;
      address: Pick<SuggestedAddress, 'address1'>;
    }
  | {
      line1Only: false;
      address: SuggestedAddress;
    };

type AddressSuggestionInputProps = {
  /** used as both the field's HTML id (label association) and its `name` attribute */
  name: string;
  /** the current value of the text input in the form state under "address1" */
  value: string;
  /** callback invoked whenever the address input changes, there are 2 signatures to handle:
   * - user has typed search text into the address input (line1Only: true) => you get only address.address1 in the data
   *   that allows you only to set the address1 field in the form state
   * - user has selected a suggested address from the dropdown (line1Only: false) => get the full address object
   *   that allows you to also set address2, city, state, postalCode, and country into the form state for other fields
   */
  onChange: (change: AddressChange) => void;
  /** label for the address input field */
  label?: string;
  /** placeholder text for the address input field */
  placeholder?: string;
  /** whether the address input field is required */
  required?: boolean;
  /** whether the address input field is disabled */
  disabled?: boolean;
  /** ISO 3166-1 alpha-2 region codes to bias/restrict suggestions (e.g. the billing country) */
  regionCodes?: string[];
  /** validation state/message to display below the input, e.g. derived from Formik's field meta
   * (`{ valid: !meta.error, message: normalizeFormikError(meta.error) }`). Pass `false` or omit to hide it. */
  validation?: FieldWrapperValidation;
  /** forwarded straight to the underlying <input> so Recurly.js can tokenize this field */
  'data-recurly'?: string;
  'data-testid'?: string;
  'data-qa-id'?: string;
};

type AddressSuggestionSegment = {
  text: string;
  isMatched: boolean;
  startIdx: number;
  endIdx: number;
};

const AddressSuggestionSubStr = ({ text, isMatched }: Pick<AddressSuggestionSegment, 'text' | 'isMatched'>) => {
  return (
    <BodyText
      as="span"
      color={isMatched ? 'default' : 'lowContrastText'}
      fontWeight={isMatched ? 'semibold' : 'regular'}
    >
      {text}
    </BodyText>
  );
};

const AddressSuggestionMenuItem = ({ value }: { value: PlaceSuggestion }) => {
  const segments = useMemo((): AddressSuggestionSegment[] => {
    // if there's not any text then return an empty list of segments
    if (!value?.text) {
      return [];
    }

    // if there are no matches to highlight, just return a single segment containing the full text
    if (!Array.isArray(value?.matches) || value?.matches?.length === 0) {
      return [{ text: value.text, isMatched: false, startIdx: 0, endIdx: value.text.length }];
    }

    // split the text into segments based on the matches
    // for each segment we need to tag with isMatched so the formatting component knows how to style it
    const addressSegments: AddressSuggestionSegment[] = [];
    let cursor = 0;

    value.matches.forEach(({ startOffset, endOffset }) => {
      if (startOffset > cursor) {
        addressSegments.push({
          text: value.text.slice(cursor, startOffset),
          isMatched: false,
          startIdx: cursor,
          endIdx: startOffset,
        });
      }

      addressSegments.push({
        text: value.text.slice(startOffset, endOffset),
        isMatched: true,
        startIdx: startOffset,
        endIdx: endOffset,
      });

      cursor = endOffset;
    });

    if (cursor < value.text.length) {
      addressSegments.push({
        text: value.text.slice(cursor),
        isMatched: false,
        startIdx: cursor,
        endIdx: value.text.length,
      });
    }

    return addressSegments;
  }, [value?.text, value?.matches]);

  if (segments?.length === 0) {
    return null;
  }

  return (
    <BodyText as="span" className="cn_truncate">
      {segments.map((segment) => (
        <AddressSuggestionSubStr
          key={`${value.id}-${segment.startIdx}-${segment.endIdx}`}
          text={segment.text}
          isMatched={segment.isMatched}
        />
      ))}
    </BodyText>
  );
};

const AddressSuggestionInput = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  required,
  disabled,
  regionCodes,
  validation,
  'data-recurly': dataRecurly,
  'data-testid': testId,
  'data-qa-id': qaId,
}: AddressSuggestionInputProps) => {
  const {
    suggestions,
    isLoading: isFetchingSuggestions,
    searchForSuggestions,
    getAddressForSuggestion,
  } = usePlacesAutocomplete({ regions: regionCodes });

  const requiredMarker = required ? ' *' : '';
  const fieldLabel = label ? `${label}${requiredMarker}` : undefined;
  // below the minimum query length nothing has been searched yet, so show the (translated) loading
  // state rather than AsyncTypeaheadInput's default "no results" message
  const isBelowMinLength = value.trim().length < PLACES_MIN_QUERY_LENGTH;

  const applySuggestion = useCallback(
    async (placeSuggestion: PlaceSuggestion[]) => {
      if (!Array.isArray(placeSuggestion) || placeSuggestion?.length !== 1) {
        return;
      }
      const suggestion = placeSuggestion?.[0];

      try {
        const address = await getAddressForSuggestion({ placeId: suggestion.id });
        onChange({ address, line1Only: false });
      } catch (error) {
        captureExceptionEvent(error instanceof Error ? error : new Error(String(error)), {
          location: 'AddressSuggestion.applySuggestion',
        });
      }
    },
    [onChange, getAddressForSuggestion]
  );

  const handleSearch = useCallback(
    (newValue: string) => {
      // set the value immediately into the form state so it reflects the user's input without waiting for the API call
      onChange({ address: { address1: newValue }, line1Only: true });
      // call the debounced search function to fetch suggestions
      searchForSuggestions(newValue);
    },
    [onChange, searchForSuggestions]
  );

  return (
    <FieldWrapper
      id={`${name}-search-input`}
      validation={validation}
      label={fieldLabel}
      disabled={disabled}
      data-testid={testId}
      data-qa-id={qaId}
    >
      <AsyncTypeaheadInput<PlaceSuggestion>
        id={name}
        testId={name}
        qaId={qaId}
        searchVal={value}
        options={suggestions}
        isLoading={isBelowMinLength || isFetchingSuggestions}
        selected={[]}
        multiple={false}
        closeOnSelect
        disabled={disabled}
        placeholder={placeholder}
        aria-label={fieldLabel}
        hideCaretIcon
        withDismissOption
        debounceDelay={0}
        labelKey="text"
        onSearch={handleSearch}
        onChange={applySuggestion}
        MenuItemComponent={AddressSuggestionMenuItem}
        data-recurly={dataRecurly}
      />
    </FieldWrapper>
  );
};

export type AddressSuggestionProps = AddressSuggestionInputProps & { region: string };

/**
 * A controlled address input that suggests addresses as the user types, backed
 * by Google's Places Autocomplete (New) API. This component provides the
 * required `PlacesApiProvider` automatically. If the provider cannot load,
 * the input remains a fully functional text field without suggestions.
 *
 * Suggestions are advisory, never required. Typing calls `onChange` with
 * `{ line1Only: true, address: { address1 } }`; selecting a suggestion calls
 * it with `{ line1Only: false, address }`, where `address` is the fully parsed
 * `SuggestedAddress`. Use `line1Only` to narrow the payload and update only
 * `address1` or autofill the remaining address fields.
 *
 * @example
 *   const { values, setFieldValue, setValues } = useFormikContext<Record<string, string>>();
 *
 *   <AddressSuggestion
 *     name="address1"
 *     region={values.country}
 *     label={t('common:form.label.addressLine1')}
 *     required
 *     value={values.address1}
 *     onChange={(change) => {
 *       if (change.line1Only === true) {
 *         setFieldValue('address1', change.address.address1);
 *         return;
 *       }
 *
 *       const { address } = change;
 *       setValues({
 *         ...values,
 *         address1: address.address1,
 *         address2: address.address2,
 *         city: address.city,
 *         state: address.state,
 *         postal_code: address.postalCode,
 *         country: address.country,
 *       });
 *     }}
 *   />
 */
export const AddressSuggestion = ({ region, ...props }: AddressSuggestionProps) => {
  return (
    <PlacesApiProvider region={region}>
      <AddressSuggestionInput {...props} />
    </PlacesApiProvider>
  );
};
